/**
 * B2 · Etapas 2–5 — CHAT DEL AULA.
 *
 * Cuatro flags gobiernan lo que se ve aquí, y cada uno apaga su parte sin
 * tocar las demás:
 *   · `academy.classroom.chat`            — el panel entero.
 *   · `academy.classroom.chat.promote`    — fijar y promover al foro.
 *   · `academy.classroom.chat.reactions`  — la fila de emojis.
 *   · `academy.classroom.chat.mentions`   — el aviso al escribir `@nombre`
 *     (aquí solo se resalta; el fan-out lo decide el servidor).
 *
 * Ocultar la interfaz es cortesía, NUNCA control de acceso: quien manda es el
 * write-path (`db/sync.rs`), que descarta en silencio —con 2xx, para no
 * congelar la cola del cliente— lo que no toque.
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { EyeOff, Lock, MessageCircle, Pin, Send, Trash2 } from "lucide-solid";
import { CHAT_REACTION_EMOJIS, groupMessagesByDay, summarizeReactions } from "@space/shared";
import type { CohortChatMessage } from "@space/powersync";
import { useI18n } from "@/providers/i18n-provider";
import { useSession } from "@/lib/auth";
import { Skeleton } from "@/components/ui/skeleton";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";
import {
  useChatActions,
  useCohortChatMessages,
  useCohortChatReactions,
  useMyChatRead,
} from "../../hooks/use-classroom-chat";
import { useCohortIdentities } from "../../hooks/use-org-identities";
import { initialsOf } from "./classroom-roster";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

/** Posición de `HH:MM` dentro de un ISO-8601 (`2026-07-28T09:15:00Z`). */
const ISO_TIME_START = 11;
const ISO_TIME_END = 16;

/** Cuánto dura silenciar un aula desde la interfaz: una semana. */
const MUTE_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

/** ¿Está esta aula silenciada ahora mismo? */
function isMuted(mutedUntil: string | null | undefined): boolean {
  return Boolean(mutedUntil) && Date.parse(mutedUntil ?? "") > Date.now();
}

export const ClassroomChat: Component<{
  cohortId: string;
  organizationId?: string | null;
  /** ¿Quien mira modera esta aula? (solo UX: la autoridad es el servidor). */
  canModerate: boolean;
  /** Candado de la cohorte: cerrado, solo escribe quien modera. */
  locked?: boolean;
}> = (props) => {
  const { t } = useI18n();
  const { userId } = useSession();
  const [body, setBody] = createSignal("");

  const messages = useCohortChatMessages(() => props.cohortId);
  const reactions = useCohortChatReactions(() => props.cohortId);
  const read = useMyChatRead(() => props.cohortId);
  const roster = useCohortIdentities(() => props.cohortId);
  const actions = useChatActions(
    () => props.cohortId,
    () => props.organizationId ?? undefined,
  );

  const promote = useFeatureEnabled("academy.classroom.chat.promote");
  const withReactions = useFeatureEnabled("academy.classroom.chat.reactions");

  const days = createMemo(() => groupMessagesByDay(messages.data ?? []));
  const canWrite = () => !props.locked || props.canModerate;

  /** Nombre para mostrar de quien escribe (o lápida si borró su cuenta). */
  const authorName = (message: CohortChatMessage) => {
    if (!message.author_id) {
      return t(msg`Former member`);
    }
    return roster.identityOf(message.author_id)?.name ?? t(msg`Classmate`);
  };

  const send = async () => {
    const text = body().trim();
    if (!text) {
      return;
    }
    setBody("");
    await actions.send(text);
    // Lo que acabo de escribir no puede contar como no leído para mí mismo.
    await actions.markRead();
  };

  return (
    <section class="space-y-3 rounded-2xl border border-border p-5" data-testid="classroom-chat">
      <div class="flex items-center justify-between gap-2">
        <h2 class="flex items-center gap-2 font-display text-lg font-bold">
          <MessageCircle class="h-5 w-5 text-accent" /> {t(msg`Classroom chat`)}
        </h2>
        <button
          type="button"
          data-testid="classroom-chat-mark-read"
          class="text-xs text-muted-foreground underline"
          onClick={() => void actions.markRead()}
        >
          {t(msg`Mark as read`)}
        </button>
      </div>

      <Show when={!messages.isLoading} fallback={<Skeleton class="h-24 w-full rounded-xl" />}>
        <Show
          when={(messages.data ?? []).length > 0}
          fallback={
            <p class="text-sm text-muted-foreground" data-testid="classroom-chat-empty">
              {t(msg`No messages yet. Say hello to your cohort.`)}
            </p>
          }
        >
          <ol class="space-y-3" data-testid="classroom-chat-messages">
            <For each={days()}>
              {(day) => (
                <li class="space-y-2">
                  <p class="text-center text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    {day.day}
                  </p>
                  <For each={day.messages}>
                    {(message) => {
                      const summary = () =>
                        summarizeReactions(reactions.data ?? [], message.id, userId() ?? "");
                      const mine = () => message.author_id === userId();
                      return (
                        <div
                          class="rounded-xl border border-border p-3"
                          data-testid="classroom-chat-message"
                          data-message-id={message.id}
                          data-hidden={message.hidden_at ? "true" : "false"}
                        >
                          <p class="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <Show
                              when={roster.identityOf(message.author_id ?? undefined)?.image}
                              fallback={
                                <span class="flex h-5 w-5 items-center justify-center rounded-full bg-accent/15 text-[9px] font-bold text-accent">
                                  {initialsOf(authorName(message))}
                                </span>
                              }
                            >
                              {(image) => (
                                <img
                                  src={image()}
                                  alt=""
                                  class="h-5 w-5 rounded-full object-cover"
                                />
                              )}
                            </Show>
                            <span class="font-semibold text-foreground">{authorName(message)}</span>
                            <span>
                              {(message.created_at ?? "").slice(ISO_TIME_START, ISO_TIME_END)}
                            </span>
                            <Show when={message.pinned_at}>
                              <Pin class="h-3 w-3 text-accent" aria-label={t(msg`Pinned`)} />
                            </Show>
                            <Show when={message.hidden_at}>
                              <span
                                class="inline-flex items-center gap-1 text-danger"
                                data-testid="classroom-chat-hidden-mark"
                              >
                                <EyeOff class="h-3 w-3" /> {t(msg`Hidden by a moderator`)}
                              </span>
                            </Show>
                            <Show when={message.promoted_thread_id}>
                              <span class="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
                                {t(msg`Now a forum thread`)}
                              </span>
                            </Show>
                          </p>
                          <p class="mt-1 whitespace-pre-line text-sm">{message.body}</p>

                          <Show when={withReactions().enabled}>
                            <div
                              class="mt-2 flex flex-wrap gap-1"
                              data-testid="classroom-chat-reactions"
                            >
                              <For each={CHAT_REACTION_EMOJIS}>
                                {(emoji) => {
                                  const count = () =>
                                    summary().counts.find((c) => c.emoji === emoji)?.count ?? 0;
                                  return (
                                    <button
                                      type="button"
                                      data-testid="classroom-chat-react"
                                      data-emoji={emoji}
                                      data-mine={summary().mine === emoji ? "true" : "false"}
                                      class="rounded-full border border-border px-2 py-0.5 text-xs"
                                      classList={{ "border-accent": summary().mine === emoji }}
                                      onClick={() =>
                                        void actions.toggleReaction(
                                          message.id,
                                          emoji,
                                          summary().mine,
                                        )
                                      }
                                    >
                                      {emoji}
                                      <Show when={count() > 0}> {count()}</Show>
                                    </button>
                                  );
                                }}
                              </For>
                            </div>
                          </Show>

                          <div class="mt-2 flex flex-wrap gap-3 text-xs">
                            <Show when={props.canModerate}>
                              <button
                                type="button"
                                data-testid="classroom-chat-hide"
                                class="text-muted-foreground underline"
                                onClick={() =>
                                  void actions.setHidden(message.id, !message.hidden_at)
                                }
                              >
                                {message.hidden_at ? t(msg`Restore`) : t(msg`Hide`)}
                              </button>
                            </Show>
                            <Show when={props.canModerate && promote().enabled}>
                              <button
                                type="button"
                                data-testid="classroom-chat-pin"
                                class="text-muted-foreground underline"
                                onClick={() =>
                                  void actions.setPinned(message.id, !message.pinned_at)
                                }
                              >
                                {message.pinned_at ? t(msg`Unpin`) : t(msg`Pin`)}
                              </button>
                            </Show>
                            <Show when={mine() || props.canModerate}>
                              <button
                                type="button"
                                data-testid="classroom-chat-delete"
                                class="inline-flex items-center gap-1 text-muted-foreground underline"
                                onClick={() => void actions.remove(message.id)}
                              >
                                <Trash2 class="h-3 w-3" /> {t(msg`Delete`)}
                              </button>
                            </Show>
                          </div>
                        </div>
                      );
                    }}
                  </For>
                </li>
              )}
            </For>
          </ol>
        </Show>
      </Show>

      <Show
        when={canWrite()}
        fallback={
          <p
            class="flex items-center gap-1.5 text-xs text-muted-foreground"
            data-testid="classroom-chat-locked"
          >
            <Lock class="h-3 w-3" /> {t(msg`This chat is closed by the academy.`)}
          </p>
        }
      >
        <div class="flex gap-2">
          <textarea
            class={field}
            rows={2}
            data-testid="classroom-chat-input"
            value={body()}
            onInput={(e) => setBody(e.currentTarget.value)}
            placeholder={t(msg`Write to your cohort…`)}
          />
          <button
            type="button"
            data-testid="classroom-chat-send"
            onClick={() => void send()}
            disabled={!body().trim()}
            class="shrink-0 self-start rounded-xl bg-accent px-4 py-2 text-sm font-bold text-accent-foreground disabled:opacity-50"
            aria-label={t(msg`Send`)}
          >
            <Send class="h-4 w-4" />
          </button>
        </div>
      </Show>

      <div class="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        {/* Copy honesto sobre el transporte: el chat viaja por sync, así que la
            entrega tarda un checkpoint. Prometer instantaneidad sería mentir. */}
        <span>{t(msg`Messages may take a few seconds to reach everyone.`)}</span>
        <button
          type="button"
          data-testid="classroom-chat-mute"
          class="underline"
          onClick={() => {
            const until = isMuted(read.data?.muted_until)
              ? null
              : new Date(Date.now() + MUTE_DURATION_MS).toISOString();
            void actions.setMuted(until);
          }}
        >
          <Show when={isMuted(read.data?.muted_until)} fallback={t(msg`Mute this classroom`)}>
            {t(msg`Unmute this classroom`)}
          </Show>
        </button>
      </div>
    </section>
  );
};
